import axios from "axios";
import { React, useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import DataTable from 'react-data-table-component';
import Navbar from "../../layouts/Navbar";
import Sidebar from "../../layouts/Sidebar";
import Footer from "../../layouts/Footer";

const ShowCategory = () => {
    const [categories, setCategories] = useState([]);
    const [filter, setFilter] = useState([]);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState('all'); // 'all', 'main', 'sub'
    const [expandedRows, setExpandedRows] = useState([]);
    const [subCategories, setSubCategories] = useState({});
    const [loadingSub, setLoadingSub] = useState(false);
    const navigate = useNavigate();

    const columns = [
        // {
        //     name: <div>ID</div>,
        //     selector: row => row.id,
        //     sortable: true,
        //     width: "8%"
        // },
        {
            name: <div>Category Title</div>,
            selector: row => row.title,
            sortable: true,
            cell: row => (
                <div>
                    <div className="fw-bold">{row.title}</div>
                    {row.sub_categories_count > 0 && (
                        <small className="text-muted">
                            <i className="bi bi-folder me-1"></i>
                            {row.sub_categories_count} year(s)
                        </small>
                    )}
                </div>
            ),
            width: "30%"
        },   
        {
            name: <div>Years</div>,
            cell: row => (
                <div>
                    {row.years && row.years.length > 0 ? (
                        <div className="d-flex flex-wrap gap-1">
                            {row.years.slice(0, 3).map((year, index) => (
                                <span key={index} className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25">
                                    {year}
                                </span>
                            ))}
                            {row.years.length > 3 && (
                                <span className="badge bg-secondary">
                                    +{row.years.length - 3} more
                                </span>
                            )}
                        </div>
                    ) : (
                        <span className="text-muted">No years</span>
                    )}
                </div>
            ),
            width: "20%"
        },
        {
            name: <div>Total Years</div>,
            selector: row => row.sub_categories_count || 0,
            sortable: true,
            width: "10%",
            center: true
        },
        {
            name: <div>Created</div>,
            selector: row => row.date,
            sortable: true,
            width: "12%"
        },    
        {
            name: <div>Actions</div>,
            cell: row => (
                <div className="d-flex gap-2">
                    <Link 
                        to={`/manage/category/${row.id}/edit/`} 
                        className="btn btn-outline-primary btn-sm"
                        title="Edit Category"
                    >
                        <i className="bi bi-pencil"></i>
                    </Link>
                    <Link 
                        to={`/admin/categories/${row.id}/years`} 
                        className="btn btn-outline-info btn-sm"
                        title="Manage Years"
                    >
                        <i className="bi bi-calendar"></i>
                    </Link>
                    <button 
                        className="btn btn-outline-danger btn-sm" 
                        onClick={() => confirmDelete(row.id, row.title)}
                        title="Delete Category"
                    >
                        <i className="bi bi-trash"></i>
                    </button>
                </div>
            ),
            width: "15%"
        }
    ];

    // Expandable row component for sub-categories
    const ExpandedComponent = ({ data }) => {
        const [subCatLoading, setSubCatLoading] = useState(false);
        const [yearDetails, setYearDetails] = useState([]);

        useEffect(() => {
            if (data.id && !subCategories[data.id]) {
                fetchSubCategories(data.id);
            } else if (subCategories[data.id]) {
                setYearDetails(subCategories[data.id]);
            }
        }, [data.id]);

        const fetchSubCategories = async (categoryId) => {
            setSubCatLoading(true);
            try {
                const response = await axios.get(
                    `https://pastq.eddcodesgh.com/qApi/admin/categories/subcategories.php?catId=${categoryId}`,
                    {
                        headers: {
                            "Content-Type": "application/json",
                            "Authorization": "Bearer f0ed28e67f2a90c8bf437f1ff949ef23655e842a2901b7b87779673a0d176506"
                        },
                        withCredentials: true
                    }
                );

                if (response.data.status === 'success') {
                    const details = response.data.records || [];
                    setYearDetails(details);
                    setSubCategories(prev => ({ ...prev, [categoryId]: details }));
                }
            } catch (error) {
                console.error("Error fetching sub-categories:", error);
            } finally {
                setSubCatLoading(false);
            }
        };

        const handleDeleteYear = async (subCatId, year) => {
            if (window.confirm(`Are you sure you want to delete year ${year}?`)) {
                try {
                    const response = await axios.delete(
                        `https://pastq.eddcodesgh.com/qApi/admin/categories/delete-year.php?id=${subCatId}`,
                        {
                            headers: {
                                "Content-Type": "application/json",
                                "Authorization": "Bearer f0ed28e67f2a90c8bf437f1ff949ef23655e842a2901b7b87779673a0d176506"
                            }
                        }
                    );

                    if (response.data.status === 200) {
                        toast.success(`Year ${year} deleted successfully!`);
                        fetchSubCategories(data.id); // Refresh
                    } else {
                        toast.error(response.data.message || "Failed to delete year");
                    }
                } catch (error) {
                    toast.error("Network error: Failed to delete year");
                }
            }
        };

        return (
            <div className="p-3 bg-light rounded-3">
                <h6 className="mb-3">
                    <i className="bi bi-calendar-week me-2"></i>
                    Year Details for "{data.title}"
                </h6>
                
                {subCatLoading ? (
                    <div className="text-center py-3">
                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <small className="text-muted ms-2">Loading year details...</small>
                    </div>
                ) : yearDetails.length > 0 ? (
                    <div className="table-responsive">
                        <table className="table table-hover table-sm">
                            <thead>
                                <tr>
                                    <th>Year ID</th>
                                    <th>Year</th>
                                    <th>Created Date</th>
                                    <th>Questions</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {yearDetails.map((year) => (
                                    <tr key={year.id}>
                                        <td>{year.id}</td>
                                        <td>
                                            <span className="badge bg-primary">
                                                {year.year}
                                            </span>
                                        </td>
                                        <td>{year.created_date || 'N/A'}</td>
                                        <td>
                                            <span className="badge bg-info">
                                                {year.question_count || 0} questions
                                            </span>
                                        </td>
                                        <td>
                                            <div className="d-flex gap-2">
                                                <Link 
                                                    to={`/admin/questions?category=${data.id}&year=${year.year}`}
                                                    className="btn btn-sm btn-outline-success"
                                                    title="View Questions"
                                                >
                                                    <i className="bi bi-eye"></i>
                                                </Link>
                                                <Link 
                                                    to={`/admin/questions/add?category=${data.id}&year=${year.year}`}
                                                    className="btn btn-sm btn-outline-warning"
                                                    title="Add Questions"
                                                >
                                                    <i className="bi bi-plus-lg"></i>
                                                </Link>
                                                <button 
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => handleDeleteYear(year.id, year.year)}
                                                    title="Delete Year"
                                                >
                                                    <i className="bi bi-trash"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="text-center py-3">
                        <i className="bi bi-calendar-x display-6 text-muted"></i>
                        <p className="text-muted mt-2 mb-3">No year details found for this category</p>
                        <Link 
                            to={`/admin/categories/${data.id}/add-year`}
                            className="btn btn-sm btn-primary"
                        >
                            <i className="bi bi-plus-circle me-2"></i>
                            Add Year
                        </Link>
                    </div>
                )}
            </div>
        );
    };

    const confirmDelete = (id, title) => {
        if (window.confirm(`Are you sure you want to delete "${title}"? This will also delete all associated years and questions.`)) {
            handleDelete(id);
        }
    };

    const handleFilter = (e) => {
        setSearch(e.target.value);
    };

    const fetchCategories = async () => {
        setLoading(true);
        try {
            const response = await axios.get('https://pastq.eddcodesgh.com/qApi/admin/categories/_viewcategories.php', {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": "Bearer f0ed28e67f2a90c8bf437f1ff949ef23655e842a2901b7b87779673a0d176506"
                },
                withCredentials: true
            });

            if (response.data.status === 'success') {
                // Process categories with years information
                const processedCategories = response.data.records.map(cat => ({
                    ...cat,
                    sub_categories_count: cat.years ? cat.years.length : 0
                }));
                setCategories(processedCategories);
                setFilter(processedCategories);
            } else {
                toast.error(response.data.message || "Failed to fetch categories");
                setCategories([]);
                setFilter([]);
            }
        } catch (error) {
            toast.error("Network error: Failed to load categories");
            console.error("Fetch error:", error);
            setCategories([]);
            setFilter([]);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            const response = await axios.delete(
                `https://pastq.eddcodesgh.com/qApi/admin/categories/delete.php?id=${id}`,
                {
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer f0ed28e67f2a90c8bf437f1ff949ef23655e842a2901b7b87779673a0d176506"
                    }
                }
            );

            if (response.data.status === 200) {
                toast.success(response.data.message || "Category deleted successfully!");
                fetchCategories(); // Refresh list
            } else {
                toast.error(response.data.message || "Failed to delete category");
            }
        } catch (error) {
            toast.error("Network error: Failed to delete category");
        }
    };

    useEffect(() => {
        fetchCategories();
    }, []);

    useEffect(() => {
        let result = categories;
        
        // Apply search filter
        if (search) {
            result = result.filter(item =>
                item.title && item.title.toLowerCase().includes(search.toLowerCase())
            );
        }
        
        // Apply view mode filter
        if (viewMode === 'main') {
            result = result.filter(item => item.sub_categories_count === 0);
        } else if (viewMode === 'sub') {
            result = result.filter(item => item.sub_categories_count > 0);
        }
        
        setFilter(result);
    }, [search, categories, viewMode]);

    const tableHeaderStyle = {
        headCells: {
            style: {
                fontWeight: 'bold',
                fontSize: '14px',
                backgroundColor: '#0d6efd',
                color: '#fff',
                padding: '12px 8px'
            }
        },
        cells: {
            style: {
                fontSize: '14px',
                padding: '12px 8px',
                verticalAlign: 'middle'
            }
        },
        rows: {
            style: {
                minHeight: '60px'
            }
        }
    };

    return (
        <>
            <Navbar />
            <Sidebar />
       
            <main id="main" className="main">
                <div className="pagetitle">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h1>Category Management</h1>
                            <nav>
                                <ol className="breadcrumb">
                                    <li className="breadcrumb-item">
                                        <Link to="/dashboard">Dashboard</Link>
                                    </li>
                                    <li className="breadcrumb-item active">Categories</li>
                                </ol>
                            </nav>
                        </div>
                        <div className="d-flex gap-2">
                            <Link to="/admin/add-category" className="btn btn-primary">
                                <i className="bi bi-plus-circle me-2"></i>
                                Add New Category
                            </Link>
                            <button 
                                className="btn btn-outline-secondary"
                                onClick={fetchCategories}
                                title="Refresh"
                            >
                                <i className="bi bi-arrow-clockwise"></i>
                            </button>
                        </div>
                    </div>
                </div>

                <section className="section">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="card">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-center mb-4">
                                        <div>
                                            <h5 className="card-title mb-0">Categories & Years</h5>
                                            <p className="text-muted mb-0">
                                                Manage your question categories and their academic years
                                            </p>
                                        </div>
                                        <div className="d-flex align-items-center gap-3">
                                            <div className="btn-group" role="group">
                                                <button
                                                    type="button"
                                                    className={`btn btn-sm ${viewMode === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                                                    onClick={() => setViewMode('all')}
                                                >
                                                    All ({categories.length})
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`btn btn-sm ${viewMode === 'main' ? 'btn-primary' : 'btn-outline-primary'}`}
                                                    onClick={() => setViewMode('main')}
                                                >
                                                    Without Years
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`btn btn-sm ${viewMode === 'sub' ? 'btn-primary' : 'btn-outline-primary'}`}
                                                    onClick={() => setViewMode('sub')}
                                                >
                                                    With Years
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {loading ? (
                                        <div className="text-center my-5 py-5">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                            <p className="mt-2 text-muted">Loading categories...</p>
                                        </div>
                                    ) : categories.length === 0 ? (
                                        <div className="text-center my-5 py-5">
                                            <i className="bi bi-folder display-1 text-muted"></i>
                                            <h4 className="mt-3">No Categories Found</h4>
                                            <p className="text-muted mb-4">Get started by adding your first category</p>
                                            <Link to="/admin/categories/create" className="btn btn-primary btn-lg">
                                                <i className="bi bi-plus-circle me-2"></i>
                                                Create First Category
                                            </Link>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="row mb-4">
                                                <div className="col-md-8">
                                                    <div className="input-group">
                                                        <span className="input-group-text bg-white">
                                                            <i className="bi bi-search text-muted"></i>
                                                        </span>
                                                        <input
                                                            type="text"
                                                            placeholder="Search categories by title..."
                                                            className="form-control border-start-0"
                                                            onChange={handleFilter}
                                                            value={search}
                                                        />
                                                        {search && (
                                                            <button 
                                                                className="btn btn-outline-secondary" 
                                                                type="button"
                                                                onClick={() => setSearch("")}
                                                            >
                                                                <i className="bi bi-x"></i>
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="col-md-4">
                                                    <div className="d-flex justify-content-between align-items-center h-100">
                                                        <div>
                                                            <span className="badge bg-primary rounded-pill px-3 py-2">
                                                                <i className="bi bi-folder me-1"></i>
                                                                Total: {categories.length}
                                                            </span>
                                                        </div>
                                                        <div className="text-muted">
                                                            Showing {filter.length} items
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <DataTable
                                                customStyles={tableHeaderStyle}
                                                columns={columns}
                                                data={filter}
                                                fixedHeader
                                                fixedHeaderScrollHeight="500px"
                                                highlightOnHover
                                                pagination
                                                paginationPerPage={10}
                                                paginationRowsPerPageOptions={[5, 10, 15, 20]}
                                                paginationComponentOptions={{
                                                    rowsPerPageText: 'Rows per page:',
                                                    rangeSeparatorText: 'of',
                                                    selectAllRowsItem: false
                                                }}
                                                expandableRows
                                                expandableRowsComponent={ExpandedComponent}
                                                expandableRowsHideExpander={false}
                                                onRowExpandToggled={(toggled, row) => {
                                                    if (toggled) {
                                                        setExpandedRows([...expandedRows, row.id]);
                                                    } else {
                                                        setExpandedRows(expandedRows.filter(id => id !== row.id));
                                                    }
                                                }}
                                                expandableRowExpanded={row => expandedRows.includes(row.id)}
                                                noDataComponent={
                                                    <div className="text-center py-5">
                                                        <i className="bi bi-search display-3 text-muted"></i>
                                                        <h4 className="mt-3">No matching categories found</h4>
                                                        <p className="text-muted">Try adjusting your search term or filter</p>
                                                    </div>
                                                }
                                                persistTableHead
                                            />
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Stats Card */}
                            <div className="row mt-4">
                                <div className="col-md-4">
                                    <div className="card border-primary">
                                        <div className="card-body">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <h6 className="card-title text-primary">Total Categories</h6>
                                                    <h2 className="mb-0">{categories.length}</h2>
                                                </div>
                                                <div className="bg-primary bg-opacity-10 rounded-3 p-3">
                                                    <i className="bi bi-folder text-primary fs-3"></i>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="card border-success">
                                        <div className="card-body">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <h6 className="card-title text-success">Categories with Years</h6>
                                                    <h2 className="mb-0">
                                                        {categories.filter(cat => cat.sub_categories_count > 0).length}
                                                    </h2>
                                                </div>
                                                <div className="bg-success bg-opacity-10 rounded-3 p-3">
                                                    <i className="bi bi-calendar-check text-success fs-3"></i>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="card border-info">
                                        <div className="card-body">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <h6 className="card-title text-info">Total Years</h6>
                                                    <h2 className="mb-0">
                                                        {categories.reduce((sum, cat) => sum + (cat.sub_categories_count || 0), 0)}
                                                    </h2>
                                                </div>
                                                <div className="bg-info bg-opacity-10 rounded-3 p-3">
                                                    <i className="bi bi-calendar-week text-info fs-3"></i>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
            
            <Footer />

            <style jsx="true">{`
                .card {
                    border-radius: 12px;
                    border: 1px solid rgba(0,0,0,0.08);
                    box-shadow: 0 4px 20px rgba(0,0,0,0.05);
                    transition: transform 0.3s ease;
                }
                
                .card:hover {
                    transform: translateY(-2px);
                }
                
                .btn-outline-primary, .btn-outline-success, .btn-outline-danger, .btn-outline-info {
                    transition: all 0.3s ease;
                }
                
                .btn-outline-primary:hover {
                    background-color: rgba(13, 110, 253, 0.1);
                }
                
                .btn-outline-success:hover {
                    background-color: rgba(25, 135, 84, 0.1);
                }
                
                .btn-outline-danger:hover {
                    background-color: rgba(220, 53, 69, 0.1);
                }
                
                .btn-outline-info:hover {
                    background-color: rgba(13, 202, 240, 0.1);
                }
                
                .badge {
                    font-weight: 500;
                }
                
                .table-hover tbody tr:hover {
                    background-color: rgba(0, 0, 0, 0.02);
                }
                
                @media (max-width: 768px) {
                    .btn-group {
                        flex-wrap: wrap;
                    }
                    
                    .btn-group .btn {
                        margin-bottom: 5px;
                    }
                    
                    .card-body {
                        padding: 1rem;
                    }
                    
                    .pagetitle h1 {
                        font-size: 1.5rem;
                    }
                }
            `}</style>
        </>
    );
};

export default ShowCategory;